import React, {useEffect, useMemo, useState} from "react";
import {MenuAddon, MenuAddonsGroup} from "../../../@types/stores/menu";
import {useTranslation} from "react-i18next";
import lodash from "lodash";
import {ReactComponent as IconCheck} from "../../../assets/icons/check.svg";
import {OrderLineAddon} from "../../../@types/stores/order";
import {getAllergenById, getCurrency, getHost, getMaxAddonsCount, getPreferenceById} from "../../../helpers/ApiHelper";
import menuStore from "../../../stores/menu";


export interface AddonsGroupProps {
    addonsGroup: MenuAddonsGroup;
    onChange: (id: number, selected: OrderLineAddon[]) => void;
    selected: OrderLineAddon[];
    free: number;
}

const AddonsGroup = ({addonsGroup, onChange, selected, free}: AddonsGroupProps) => {
    const maxAddonsCount = getMaxAddonsCount();

    const [t] = useTranslation();

    const [addons, setAddons] = useState<OrderLineAddon[]>([]);
    const [groupFree, setGroupFree] = useState<number>(0);

    useEffect(() => {
        setHeightToShortBlocks();
    }, []);

    useEffect(() => {
        if (!lodash.isEqual(selected, addons)) {
            setAddons(selected);
            calculateFree();
        }
    }, [selected]);

    useEffect(() => {
        calculateFree();
    }, [free]);

    const setHeightToShortBlocks = () => {
        const allTagContainers = document.querySelectorAll(".tagContainer.short");
        const tagsByGroupId: {id: string; height: number}[] = [];
        allTagContainers.forEach(tag => {
            const id = tag.attributes.getNamedItem("x-data-groupId")?.value;
            if (typeof id !== "undefined") {
                const height = tag.clientHeight;

                const existedTag = tagsByGroupId.find(x => x.id === id);
                if (!existedTag) {
                    tagsByGroupId.push({id, height});
                }
                else if (existedTag.height < height) {
                    existedTag.height = height;
                }
            }
        });

        tagsByGroupId.forEach(tag => {
            document.querySelectorAll(`.tagContainer.short[x-data-groupId="${tag.id}"]`).forEach(dom => {
                // @ts-ignore
                dom.style.height = `${tag.height}px`;
            });
        });

    }

    const hasImage = useMemo(() => {
        for (const a of addonsGroup.items) {
            if (a.previewList)
                return true;
        }
        return false;
    }, [addonsGroup]);

    const calculateFree = () => {
        const _groupFree = (addonsGroup.freeAddons || 0) - selected.length > 0 ? (addonsGroup.freeAddons || 0) - selected.length : 0;
        const allowedFree = free <= _groupFree ? free : _groupFree;
        setGroupFree(allowedFree);
    }

    const changeAddons = (addon: MenuAddon) => {
        if (maxAddonsCount === 1) {
            const _addon: OrderLineAddon = {
                id: addon.id,
                number: addon.number,
                quantity: 1,
                price: addon.price
            };
            setAddons([_addon]);
            onChange(addonsGroup.id, [_addon]);
        } else {
            let newAddons = lodash.cloneDeep(addons);
            if (newAddons.some(x => x.id === addon.id))
                newAddons = newAddons.filter(x => x.id !== addon.id);
            else {
                if (newAddons.length < maxAddonsCount) {
                    const _addon: OrderLineAddon = {
                        id: addon.id,
                        number: addon.number,
                        quantity: 1,
                        price: addon.price
                    };
                    newAddons.push(_addon);
                }
            }
            setAddons(newAddons);
            onChange(addonsGroup.id, newAddons);
        }
    }

    return <div className="AddonsGroup">
        <div className="text">
            {addonsGroup.name}
            <span className="min-max">
                {t("max")}: {maxAddonsCount - selected.length}
                {groupFree > 0 ? <span className="free">{t("free")}: {groupFree}</span> : ""}
            </span>
        </div>
        <div>
            <div className="blocks">
                {addonsGroup.items.map((a: MenuAddon) =>
                    <div key={`addonsGroup_${addonsGroup.name}_addon_${a.id}`}
                         className={`block ${addons.some(x => x.id === a.id) ? "active" : ""}`}
                         onClick={() => changeAddons(a)}>
                        {addons.some(x => x.id === a.id)
                            ? <IconCheck className="check"/>
                            : null
                        }
                        {hasImage
                            ? <>
                                <div className={`img ${a.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).length ? "blur" : ""}`}>
                                    {a.previewList
                                        ? <img alt={a.name} src={`${getHost()}${a.previewList}`}/>
                                        : null
                                    }
                                </div>
                                <div className="tagsImages">
                                    {a.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y)?.image ?
                                        <img key={`allergenTagImage_${y}`}
                                             src={`${getHost()}${getAllergenById(y)?.image || ""}`}
                                             alt={getAllergenById(y)?.name}/> : null)}
                                    {a.preferences?.filter(y => menuStore.filter.preferences.length && menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y)?.image ?
                                        <img key={`preferenceTagImage_${y}`}
                                             src={`${getHost()}${getPreferenceById(y)?.image || ""}`}
                                             alt={getPreferenceById(y)?.name}/> : null)}
                                </div>
                            </>
                            : null
                        }
                        <div className="text">
                            <div x-data-groupId={`addonsGroup_${addonsGroup.id}`} className={`tagContainer ${!hasImage ? "short" : ""}`}>
                                <div className="tags">
                                    {a.allergens?.filter(y => menuStore.filter.allergens.indexOf(y) !== -1).map(y => getAllergenById(y) ?
                                        <div key={`allergenTag_${y}`}
                                             className="tag allergen">{getAllergenById(y)?.name || ""}</div> : null)}
                                    {a.preferences?.filter(y => menuStore.filter.preferences.length && menuStore.filter.preferences.indexOf(y) !== -1).map(y => getPreferenceById(y) ?
                                        <div key={`preferenceTag_${y}`}
                                             className="tag preference">{getPreferenceById(y)?.name || ""}</div> : null)}
                                </div>
                            </div>
                            <div className="title">
                                {a.name}
                            </div>
                            {a.price
                                ? <div className="price">
                                    + {a.price.toFixed(2)} {getCurrency()}
                                </div>
                                : null
                            }
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
}

export default AddonsGroup;
